/* How the app decides two records are the same one, entered twice.
 *
 * The office types "Kedron Cabinets" on Tuesday and "kedron cabinets" on
 * Friday, or the same mobile with the spaces in different places, and ends up
 * with two customers whose jobs no longer add up. So a customer is matched on a
 * normalised key rather than on what was typed.
 *
 * These live in shared, and they are runtime functions rather than types,
 * because both sides need the same answer: the form says a customer already
 * exists before the request goes out, and the server refuses the write when it
 * arrives. A form that normalised differently from the server would either miss
 * a duplicate or warn about one the server then accepted. */
/** Emails match lower-cased and trimmed — nobody has two accounts because they
 *  typed their address with a capital letter. */
export function emailKey(value) {
    return (value ?? '').trim().toLowerCase();
}
/** Phone numbers match on their digits, so `0412 884 019`, `0412884019` and
 *  `(04) 1288 4019` are one number.
 *
 *  Australian numbers are also written `+61 412 884 019` — the same number with
 *  the leading 0 replaced by the country code — so a `61` prefix becomes that 0
 *  again before comparing. Anything else is left as its digits: this shop rings
 *  Brisbane, and guessing at other countries' dialling rules would match numbers
 *  that are not the same. */
export function phoneKey(value) {
    const digits = (value ?? '').replace(/\D/g, '');
    if (digits.startsWith('0061') && digits.length === 13)
        return `0${digits.slice(4)}`;
    if (digits.startsWith('61') && digits.length === 11)
        return `0${digits.slice(2)}`;
    return digits;
}
/** Names match case-insensitively, with runs of whitespace flattened. Deliberately
 *  no more than that: "Ferro Kitchens" and "Ferro Kitchens Pty Ltd" are one
 *  business to the shop but two names, and a rule clever enough to merge them
 *  would sooner or later merge two it shouldn't. */
export function nameKey(value) {
    return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}
/** Colour codes match upper-cased, with runs of whitespace flattened, so
 *  `pn1g7` is PN1G7 and `T12  40.A1` is T12 40.A1. The code is what gets
 *  re-ordered, so one code is one colour — the same code on the list twice
 *  would split its jobs between two rows, and a re-order could go out for
 *  either. No more than that, for the reason `nameKey` gives: `T1240.A1` might
 *  be a different paint. */
export function colourKey(value) {
    return (value ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
}
/** How each match reads in a sentence: "Kedron Cabinets already has that email
 *  address." */
export const MATCH_FIELD_LABEL = {
    name: 'name',
    email: 'email address',
    phone: 'phone number',
};
/** "that email address", "that email address and phone number" — the middle of
 *  the sentence both the server and the form put a duplicate into. */
export function matchFieldList(fields) {
    const labels = fields.map((f) => MATCH_FIELD_LABEL[f]);
    if (labels.length <= 1)
        return labels[0] ?? '';
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
//# sourceMappingURL=matching.js.map